// Shared slide navigation engine
// Usage: const engine = initEngine(rootEl, { initialSlide: 2 }); engine.goTo(3);
// Slide index is owned by the React router (path-based), not the URL hash.

import { isMobileViewport } from './viewport.js';

export function initEngine(rootEl, opts = {}) {
  const isMobile = isMobileViewport();
  const slides = rootEl.querySelectorAll('.slide');
  const total = slides.length;
  const initial = Number.isInteger(opts.initialSlide) ? opts.initialSlide : 0;
  let current = isMobile ? 0 : (initial >= 0 && initial < total ? initial : 0);

  const slideCallbacks = new Map();
  let callbackId = 0;

  function onSlideChange(callback) {
    const id = callbackId++;
    slideCallbacks.set(id, callback);
    return id;
  }

  function offSlideChange(id) {
    slideCallbacks.delete(id);
  }

  function getCurrentSlide() {
    return current;
  }

  function animateEvalBars() {
    rootEl.querySelectorAll('.eval-fill').forEach(bar => {
      bar.style.width = '0';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          bar.style.width = bar.dataset.width + '%';
        });
      });
    });
  }

  function goTo(n) {
    if (n < 0 || n >= total) return;
    if (n === current) return; // idempotent — lets the router drive without loops
    if (current >= 0 && slides[current]) slides[current].classList.remove('active');
    current = n;
    slides[current].classList.add('active');

    const pageNum = rootEl.querySelector('#pageNum');
    const ctrlNum = rootEl.querySelector('#ctrlNum');
    const prevBtn = rootEl.querySelector('#prevBtn');
    const nextBtn = rootEl.querySelector('#nextBtn');

    if (pageNum) pageNum.textContent = `${current + 1} / ${total}`;
    if (ctrlNum) ctrlNum.textContent = `${current + 1} / ${total}`;
    if (prevBtn) prevBtn.disabled = current === 0;
    if (nextBtn) nextBtn.disabled = current === total - 1;

    // Trigger eval bar animation on the eval slide (find by content)
    const slideEl = slides[current];
    if (slideEl && slideEl.querySelector('.eval-fill')) {
      animateEvalBars();
    }

    // Update nav colors for dark slides
    const isDark = slides[current].classList.contains('dark');
    const site = rootEl.querySelector('.nav-bar .site');
    const pageNumEl = rootEl.querySelector('.nav-bar .page-num');
    if (site) site.style.color = isDark ? '#555' : '#aaa';
    if (pageNumEl) pageNumEl.style.color = isDark ? '#555' : '#aaa';

    // Notify registered callbacks (React owns the URL)
    slideCallbacks.forEach(cb => cb(current));
  }

  // --- Event handlers (stored for cleanup) ---
  const handlers = [];

  function addListener(target, event, handler, options) {
    target.addEventListener(event, handler, options);
    handlers.push({ target, event, handler, options });
  }

  // --- Image lightbox (all viewports): click image -> blurred overlay, click outside / Esc to close ---
  function setupLightbox() {
    const deckEl = rootEl.querySelector('#deck') || rootEl.querySelector('.deck') || rootEl;
    function close() {
      const el = rootEl.ownerDocument.querySelector('.lightbox-overlay');
      if (!el) return;
      el.classList.remove('open');
      setTimeout(() => el.remove(), 200);
    }
    function open(src, alt) {
      close();
      const doc = rootEl.ownerDocument;
      const overlay = doc.createElement('div');
      overlay.className = 'lightbox-overlay';
      const img = doc.createElement('img');
      img.src = src;
      img.alt = alt || '';
      img.className = 'lightbox-img';
      overlay.appendChild(img);
      overlay.addEventListener('click', e => { if (e.target === overlay) close(); });
      doc.body.appendChild(overlay);
      requestAnimationFrame(() => overlay.classList.add('open'));
    }
    deckEl.querySelectorAll('img').forEach(img => {
      img.style.cursor = 'zoom-in';
      addListener(img, 'click', e => {
        e.stopPropagation();
        open(img.currentSrc || img.src, img.alt);
      });
    });
    addListener(document, 'keydown', e => { if (e.key === 'Escape') close(); });
  }
  setupLightbox();

  if (!isMobile) {
    // Button controls
    const prevBtn = rootEl.querySelector('#prevBtn');
    const nextBtn = rootEl.querySelector('#nextBtn');
    if (prevBtn) addListener(prevBtn, 'click', e => { e.stopPropagation(); goTo(current - 1); });
    if (nextBtn) addListener(nextBtn, 'click', e => { e.stopPropagation(); goTo(current + 1); });

    // Keyboard
    const keyHandler = e => {
      if (e.target.tagName === 'BUTTON' || e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); goTo(current + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(current - 1); }
      if (e.key === 'f' || e.key === 'F') {
        if (document.fullscreenElement) document.exitFullscreen();
        else document.documentElement.requestFullscreen?.();
      }
    };
    addListener(document, 'keydown', keyHandler);

    // Click navigation: left third -> prev, right third -> next, middle -> none.
    // -1 = prev zone, 1 = next zone, 0 = neutral (or over interactive content).
    const deck = rootEl.querySelector('#deck') || rootEl.querySelector('.deck') || rootEl;
    const caret = d => `url("data:image/svg+xml,${encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke-linecap="round" stroke-linejoin="round"><path stroke="#fff" stroke-width="4" d="${d}"/><path stroke="#1A1A1A" stroke-width="2" d="${d}"/></svg>`,
    )}") 16 16`;
    const CURSOR_PREV = `${caret('m15 18-6-6 6-6')}, w-resize`;
    const CURSOR_NEXT = `${caret('m9 18 6-6-6-6')}, e-resize`;
    const navZone = e => {
      // Only bare background counts: the deck/root itself or a slide's own
      // padding area. Any content element (text, cards, canvas, …) is inert.
      const t = e.target;
      const isBackground = t === deck || t === rootEl || (t.classList && t.classList.contains('slide'));
      if (!isBackground) return 0;
      const sel = window.getSelection();
      if (sel && !sel.isCollapsed) return 0;
      const third = window.innerWidth / 3;
      if (e.clientX < third) return -1;
      if (e.clientX > third * 2) return 1;
      return 0;
    };
    addListener(deck, 'click', e => {
      const zone = navZone(e);
      if (zone === -1) goTo(current - 1);
      else if (zone === 1) goTo(current + 1);
    });
    addListener(deck, 'mousemove', e => {
      const zone = navZone(e);
      deck.style.cursor = zone === -1 ? CURSOR_PREV : zone === 1 ? CURSOR_NEXT : '';
    });

    // Touch swipe
    let touchX = 0;
    addListener(document, 'touchstart', e => { touchX = e.touches[0].clientX; });
    addListener(document, 'touchend', e => {
      const dx = e.changedTouches[0].clientX - touchX;
      if (Math.abs(dx) > 50) dx > 0 ? goTo(current - 1) : goTo(current + 1);
    });

    // Initialize: clear any markup-default active slide, then go to the target
    // from a sentinel so the (now idempotent) goTo always applies.
    const target = current;
    slides.forEach(s => s.classList.remove('active'));
    current = -1;
    goTo(target);
  } else {
    // Mobile scroll mode — make all slides visible
    slides.forEach(s => s.classList.add('active'));
  }

  function destroy() {
    handlers.forEach(({ target, event, handler, options }) => {
      target.removeEventListener(event, handler, options);
    });
    handlers.length = 0;
    slideCallbacks.clear();
    rootEl.ownerDocument.querySelector('.lightbox-overlay')?.remove();
  }

  return {
    onSlideChange,
    offSlideChange,
    getCurrentSlide,
    goTo,
    destroy,
    get total() { return total; },
  };
}
